import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  House, ForkKnife, Tree, Hammer, Wrench, Briefcase, Car,
  Lightning, Truck, HardHat, Tooth, Tractor, HandCoins, Factory, Buildings,
  ArrowRight, Coins, Receipt, UsersThree, ShieldCheck,
} from "@phosphor-icons/react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { PRESETS, PRESET_BY_ID } from "@/lib/presets";
import { fmtCurrency } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  House, ForkKnife, Tree, Hammer, Wrench, Briefcase, Car,
  Lightning, Truck, HardHat, Tooth, Tractor, HandCoins, Factory, Buildings,
};

export default function Examples() {
  const nav = useNavigate();
  const [activeId, setActiveId] = useState("contractor");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const active = useMemo(() => PRESET_BY_ID[activeId], [activeId]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    axios
      .post(`${API}/calculate`, active.inputs)
      .then(({ data }) => setResult(data))
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [activeId, active]);

  const useInCalculator = () => {
    sessionStorage.setItem("bab_preset_inputs", JSON.stringify(active.inputs));
    nav("/calculator");
  };

  return (
    <main data-testid="examples-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <div className="mb-10">
        <span className="label-eyebrow">Quick estimates</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
          Industry examples
        </h1>
        <p className="mt-3 text-brand-ink/70 max-w-2xl">
          Pick a typical client profile to see what a "before vs. after" looks like with
          baseline assumptions. Then open it in the calculator and tune for your real client.
        </p>
      </div>

      {/* Dropdown (mobile-friendly) + horizontal preset chips (desktop) */}
      <div className="mb-8">
        <div className="md:hidden">
          <Select value={activeId} onValueChange={setActiveId}>
            <SelectTrigger className="bg-white border-brand-line h-12 rounded-md" data-testid="examples-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white border-brand-line">
              {PRESETS.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="hidden md:grid grid-cols-3 lg:grid-cols-5 gap-2">
          {PRESETS.map((p) => {
            const Icon = ICONS[p.icon] || Briefcase;
            const isActive = p.id === activeId;
            return (
              <button
                key={p.id}
                data-testid={`preset-${p.id}`}
                onClick={() => setActiveId(p.id)}
                className={`flex flex-col items-center gap-2 p-4 rounded-md border transition-all ${
                  isActive
                    ? "bg-brand-forest text-brand-bone border-brand-forest"
                    : "bg-white text-brand-ink/80 border-brand-line hover:border-brand-forest hover:-translate-y-0.5"
                }`}
              >
                <Icon size={22} weight="duotone" />
                <span className="text-[11px] font-semibold leading-tight text-center">
                  {p.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Active preset card */}
      <section className="bg-white border border-brand-line rounded-md p-6 md:p-10">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <span className="label-eyebrow">Profile</span>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-brand-forest leading-tight">
              {active.label}
            </h2>
            <p className="mt-3 text-brand-ink/70">{active.blurb}</p>
            {active.note && (
              <p className="mt-3 text-xs italic text-brand-muted">{active.note}</p>
            )}

            <div className="mt-7 pt-6 border-t border-brand-line space-y-2.5 text-sm">
              <Row label="Annual savings / retained earnings" value={fmtCurrency(active.inputs.annual_contribution)} />
              <Row label="Vehicles / equipment" value={active.inputs.num_vehicles} />
              <Row label="Avg monthly payment" value={fmtCurrency(active.inputs.avg_monthly_payment)} />
              <Row label="Avg loan rate" value={`${(active.inputs.avg_loan_interest_rate * 100).toFixed(2)}%`} />
              <Row label="Key employees" value={active.inputs.num_key_employees} />
              <Row label="Tax assumption" value={`${(active.inputs.passive_tax_rate * 100).toFixed(2)}%`} />
            </div>

            <Button
              onClick={useInCalculator}
              data-testid="btn-use-preset"
              className="mt-8 bg-brand-forest hover:bg-brand-forestHover text-brand-bone rounded-md gap-2 h-11 px-6"
            >
              Open in calculator <ArrowRight size={16} weight="bold" />
            </Button>
          </div>

          <div className="lg:col-span-7">
            {loading || !result ? (
              <div className="h-72 bg-brand-cream/40 rounded-md animate-pulse" />
            ) : (
              <>
                <div className="bg-brand-forest text-brand-bone rounded-md p-7 grain relative overflow-hidden mb-5">
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-brand-bone/60">
                      {active.inputs.horizon_years}-year wealth gain
                    </div>
                    <div className="mt-3 font-serif text-4xl md:text-5xl text-brand-bone leading-tight break-words">
                      {fmtCurrency(result.headline_wealth_gain)}
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <SubTile label="5-yr" value={fmtCurrency(result.summary_5yr.total_wealth_gain)} />
                      <SubTile label="10-yr" value={fmtCurrency(result.summary_10yr.total_wealth_gain)} accent />
                    </div>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <Mini icon={Coins} label="Tax drag saved" value={fmtCurrency(result.headline_tax_drag_saved)} />
                  <Mini icon={Receipt} label="Interest recaptured" value={fmtCurrency(result.headline_interest_recaptured)} />
                  <Mini icon={UsersThree} label="Retention value" value={fmtCurrency(result.headline_retention_value)} />
                  <Mini icon={ShieldCheck} label="IBC cash value" value={fmtCurrency(result.headline_ibc_cash_value)} />
                </div>
                <p className="mt-5 text-xs text-brand-muted leading-relaxed">
                  Estimates only — based on industry-average assumptions for the chosen profile.
                  Real results depend on the client's actual numbers, the participating dividend
                  scale declared annually by the issuing insurer, and applicable tax rules.
                </p>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-brand-ink/65">{label}</span>
      <span className="text-brand-forest font-semibold tabular-nums">{value}</span>
    </div>
  );
}

function SubTile({ label, value, accent }) {
  return (
    <div className={`rounded-md p-3 border ${accent ? "bg-brand-copper/20 border-brand-copper/40" : "bg-brand-bone/10 border-brand-bone/20"}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] text-brand-bone/60">{label}</div>
      <div className={`mt-1 font-serif text-xl ${accent ? "text-brand-copper" : "text-brand-bone"}`}>{value}</div>
    </div>
  );
}

function Mini({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-brand-line rounded-md p-4">
      <div className="flex items-center gap-2 text-brand-forest">
        <Icon size={16} weight="duotone" />
        <span className="text-[10px] uppercase tracking-[0.16em]">{label}</span>
      </div>
      <div className="mt-2 font-serif text-xl text-brand-forest">{value}</div>
    </div>
  );
}
